import {AppLanguage} from 'platform/locale';

import {always, isNil, map, pipe, reject, tryCatch} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {Nullish} from 'shared/web';

import {getLanguageOfInstructions} from '../(sections)/Documentation/utils/getLanguageOfInstructions';
import {getTechnicalCertificate} from '../(sections)/Documentation/utils/getTechnicalCertificate';
import {getVehicleModifiedAgainstProductionTuning} from '../(sections)/Documentation/utils/getVehicleModifiedAgainstProductionTuning';
import {getVehicleUnderFactoryWarranty} from '../(sections)/Documentation/utils/getVehicleUnderFactoryWarranty';
import {
  AuditCategory,
  AuditStructure,
  RelatedAction,
  VehicleAudit,
} from '../api/digitalCertificateApi';
import {getAdditionalRelatedActionsByUniqueKeys} from './getAdditionalRelatedActionsByUniqueKeys';
import {getAuditFieldLabel} from './getAuditFieldLabel';
import {getAuditFieldValue} from './getAuditFieldValue';
import {getAuditFieldValueWithUnit} from './getAuditFieldValueWithUnit';
import {getRelatedActionByComparisonKey} from './getRelatedActionByComparisonKey';
import {getRelatedActionsByUniqueKeys} from './getRelatedActionsByUniqueKeys';
import {getServiceBookState} from './getServiceBookState';
import {isCategoryEnabledInAudit} from './isCategoryEnabledInAudit';

interface DocumentationParams {
  language: AppLanguage;
  vehicleAudit: VehicleAudit | Nullish;
}

export function getDocumentation(params: DocumentationParams) {
  if (!params.vehicleAudit || !params.vehicleAudit.audit || !params.vehicleAudit.auditStructure) {
    return null;
  }

  const relatedActions = getRelatedActionsByUniqueKeys({
    parentUniqueKey: 'VEHICLE_DOCUMENTATION',
    categories: params.vehicleAudit.auditStructure.categories,
  });

  const additionalActions = getAdditionalRelatedActionsByUniqueKeys({
    parentUniqueKey: 'VEHICLE_DOCUMENTATION',
    categories: params.vehicleAudit.auditStructure.categories,
  });

  if (!relatedActions?.length || !additionalActions?.length) {
    return null;
  }

  return {
    vin: getVin({
      relatedActions,
      additionalActions,
      auditCategories: params.vehicleAudit.audit.categories,
      auditStructure: params.vehicleAudit.auditStructure,
    }),
    status: getAuditFieldValue({
      key: 'documentationStatus',
      relatedActions,
      auditCategories: params.vehicleAudit.audit.categories,
    }),
    dateOfManufacture: getDateOfManufacture({
      relatedActions,
      additionalActions,
      auditCategories: params.vehicleAudit.audit.categories,
      auditStructure: params.vehicleAudit.auditStructure,
    }),
    firstRegistration: getFirstRegistration({
      relatedActions,
      additionalActions,
      auditCategories: params.vehicleAudit.audit.categories,
      auditStructure: params.vehicleAudit.auditStructure,
    }),
    serviceBookType: getAuditFieldLabel({
      key: 'documentationServiceBookType',
      relatedActions,
      auditCategories: params.vehicleAudit.audit.categories,
      language: params.language,
    }),
    // T20-125652: legacy BE structure, kept until every audit nests SERVICE_BOOK under
    // SERVICE_HISTORY.
    serviceBookState: getServiceBookState({
      relatedActions,
      auditCategories: params.vehicleAudit.audit.categories,
      language: params.language,
      parentUniqueKey: 'VEHICLE_DOCUMENTATION',
      auditStructure: params.vehicleAudit.auditStructure,
    }),
    currentMileage: getCurrentMileage({
      relatedActions,
      additionalActions,
      auditCategories: params.vehicleAudit.audit.categories,
      auditStructure: params.vehicleAudit.auditStructure,
    }),
    cocListStatus: {
      value: getAuditFieldLabel({
        key: 'documentationCocListStatus',
        relatedActions,
        auditCategories: params.vehicleAudit.audit.categories,
        language: params.language,
      }),
      isEnabledInAudit: isCategoryEnabledInAudit({
        categories: params.vehicleAudit.auditStructure?.categories ?? [],
        parentUniqueKey: 'VEHICLE_DOCUMENTATION',
        uniqueKey: 'CERTIFICATE_OF_CONFORMITY',
      }),
    },
    technicalCert: getTechnicalCertificate({
      key: 'documentationTechnicalCertStatus',
      relatedActions,
      auditCategories: params.vehicleAudit.audit.categories,
      language: params.language,
      auditStructure: params.vehicleAudit.auditStructure,
    }),
    vehicleUnderFactoryWarranty: getVehicleUnderFactoryWarranty({
      key: 'documentationVehicleUnderFactoryWarrantyStatus',
      relatedActions,
      auditCategories: params.vehicleAudit.audit.categories,
      language: params.language,
      auditStructure: params.vehicleAudit.auditStructure,
    }),
    operatingInstructions: getAuditFieldLabel({
      key: 'documentationOperatingInstructions',
      relatedActions,
      auditCategories: params.vehicleAudit.audit.categories,
      language: params.language,
    }),
    languageOfInstructions: getLanguageOfInstructions({
      key: 'documentationOperatingInstructions',
      relatedActions,
      auditCategories: params.vehicleAudit.audit.categories,
      language: params.language,
      auditStructure: params.vehicleAudit.auditStructure,
    }),
    mandatoryEquipmentAndAccessories: getMandatoryEquipmentAndAccessories({
      relatedActions,
      additionalActions,
      auditCategories: params.vehicleAudit.audit.categories,
      language: params.language,
      auditStructure: params.vehicleAudit.auditStructure,
    }),
    vehicleModifiedAgainstProductionTuning: getVehicleModifiedAgainstProductionTuning({
      relatedActions,
      auditCategories: params.vehicleAudit.audit.categories,
      auditStructure: params.vehicleAudit.auditStructure,
      language: params.language,
    }),
  };
}

type DocumentationUtilParams = {
  relatedActions: RelatedAction[];
  additionalActions: RelatedAction[];
  auditCategories: AuditCategory[];
  auditStructure?: AuditStructure;
};
const getVin = (params: DocumentationUtilParams) => {
  const isEnabledInAudit = isCategoryEnabledInAudit({
    categories: params.auditStructure?.categories ?? [],
    parentUniqueKey: 'VEHICLE_DOCUMENTATION',
    uniqueKey: 'VEHICLE_IDENTIFICATION_NUMBER',
  });

  if (isNil(params.relatedActions)) {
    return {isEnabledInAudit};
  }

  return {
    value: getAuditFieldValue({
      key: 'documentationVin',
      relatedActions: params.relatedActions,
      auditCategories: params.auditCategories,
    }),
    comment: getAuditFieldValue({
      key: 'documentationVinComment',
      relatedActions: params.additionalActions,
      auditCategories: params.auditCategories,
    }),
    isEnabledInAudit,
  };
};
const getDateOfManufacture = (params: DocumentationUtilParams) => {
  const isEnabledInAudit = isCategoryEnabledInAudit({
    categories: params.auditStructure?.categories ?? [],
    parentUniqueKey: 'VEHICLE_DOCUMENTATION',
    uniqueKey: 'YEAR_OF_PRODUCTION',
  });

  if (isNil(params.relatedActions)) {
    return {isEnabledInAudit};
  }

  return {
    value: getAuditFieldValue({
      key: 'documentationDateOfManufacture',
      relatedActions: params.relatedActions,
      auditCategories: params.auditCategories,
    }),
    comment: getAuditFieldValue({
      key: 'documentationDateOfManufactureComment',
      relatedActions: params.additionalActions,
      auditCategories: params.auditCategories,
    }),
    isEnabledInAudit,
  };
};
const getFirstRegistration = (params: DocumentationUtilParams) => {
  const isEnabledInAudit = isCategoryEnabledInAudit({
    categories: params.auditStructure?.categories ?? [],
    parentUniqueKey: 'VEHICLE_DOCUMENTATION',
    uniqueKey: 'DATE_OF_FIRST_REGISTRATION',
  });

  if (isNil(params.relatedActions)) {
    return {isEnabledInAudit};
  }

  return {
    value: getAuditFieldValue({
      key: 'documentationFirstRegistration',
      relatedActions: params.relatedActions,
      auditCategories: params.auditCategories,
    }),
    comment: getAuditFieldValue({
      key: 'documentationFirstRegistrationComment',
      relatedActions: params.additionalActions,
      auditCategories: params.auditCategories,
    }),
    isEnabledInAudit,
  };
};
const getCurrentMileage = (params: DocumentationUtilParams) => {
  const isEnabledInAudit = isCategoryEnabledInAudit({
    categories: params.auditStructure?.categories ?? [],
    parentUniqueKey: 'VEHICLE_DOCUMENTATION',
    uniqueKey: 'CURRENT_MILEAGE',
  });

  if (isNil(params.relatedActions)) {
    return {isEnabledInAudit};
  }

  return {
    value: getAuditFieldValueWithUnit({
      key: 'documentationCurrentMileage',
      relatedActions: params.relatedActions,
      auditCategories: params.auditCategories,
    }),
    comment: getAuditFieldValue({
      key: 'documentationCurrentMileageComment',
      relatedActions: params.additionalActions,
      auditCategories: params.auditCategories,
    }),
    isEnabledInAudit,
  };
};
const getMandatoryEquipmentAndAccessories = (
  params: DocumentationUtilParams & {language: AppLanguage}
) => {
  const relatedAction = getRelatedActionByComparisonKey(
    'documentationMandatoryEquipmentAndAccessories',
    params.relatedActions
  );
  const auditValueKeys = getAuditFieldValue({
    key: 'documentationMandatoryEquipmentAndAccessories',
    relatedActions: params?.relatedActions,
    auditCategories: params.auditCategories,
  });

  const isEnabledInAudit = isCategoryEnabledInAudit({
    categories: params.auditStructure?.categories ?? [],
    parentUniqueKey: 'VEHICLE_DOCUMENTATION',
    uniqueKey: 'MANDATORY_EQUIPMENT_AND_BASIC_ACCESSORIES',
  });

  if (!relatedAction?.values?.length || isNilOrEmpty(auditValueKeys)) {
    return {isEnabledInAudit};
  }

  const getFieldLabel = (keyValue: string) => {
    const auditStructureValue = relatedAction.values?.find((v) => v.value === keyValue);
    return auditStructureValue?.label?.[params.language] ?? null;
  };

  return {
    value: pipe(
      tryCatch(JSON.parse, always([])),
      map(getFieldLabel),
      reject(isNilOrEmpty),
      (items) => (items?.length ? items.join(', ') : null)
    )(auditValueKeys!),
    comment: getAuditFieldValue({
      key: 'documentationMandatoryEquipmentAndAccessoriesComment',
      relatedActions: params.additionalActions,
      auditCategories: params.auditCategories,
    }),
    isEnabledInAudit,
  };
};
